// =============================================================================
// SIREPPAR - Bloque "Estado de la verificación documental" de los informes
// =============================================================================
// Los textos precargados de los informes al Secretario y al TEP afirman que la
// documentación está en condiciones. Eso es cierto en el camino feliz, pero la
// supervisora puede querer elevar igual e informarle qué quedó mal. Este bloque
// se agrega al final del cuerpo (bloque gestionado, como los atributos objetados
// y los antecedentes) y detalla qué documentación no está aprobada.
//
// Se distinguen CUATRO situaciones, que no son lo mismo y en el informe no
// pueden ir mezcladas:
//   - APROBADA:      revisada y aprobada. No se informa (es lo esperado).
//   - RECHAZADA:     revisada y rechazada, con su motivo.
//   - SIN VERIFICAR: está cargada en el sistema pero nadie la revisó todavía
//                    (estado PENDIENTE). No es lo mismo que faltar.
//   - SIN PRESENTAR: no se cargó ningún archivo de ese tipo.
//
// De ahí que el encabezado hable de documentación "aprobada" y no "verificada":
// una documentación puede estar verificada (revisada) y sin embargo rechazada.
//
// Qué se mira y qué NO:
//   - SÍ: la documentación obligatoria de la etapa y el DNI de las personas que
//     esa etapa exige (ver armadoVerificacion en ExpedienteDetailPage).
//   - NO: "Otros documentos", "Emblemas y símbolos" ni "Documentación TEP": no
//     son documentación exigida, así que no corresponde informarlos.
// =============================================================================

import { MARKER_VERIFICACION, aplicarBloqueGestionado } from './bloquesInforme';

export type EstadoVerificacion = 'APROBADA' | 'RECHAZADA' | 'SIN_VERIFICAR' | 'SIN_PRESENTAR';

export interface ItemVerificacion {
  label: string;
  estado: EstadoVerificacion;
  motivo?: string;
}

// Grupos del detalle, en el orden en que se listan. APROBADA no se lista.
const GRUPOS: Array<{ estado: EstadoVerificacion; titulo: string }> = [
  { estado: 'RECHAZADA', titulo: 'Documentación rechazada' },
  { estado: 'SIN_PRESENTAR', titulo: 'Documentación no presentada' },
  { estado: 'SIN_VERIFICAR', titulo: 'Documentación presentada pero sin verificar' },
];

/**
 * Arma el bloque de advertencia a partir de los ítems evaluados. Devuelve ''
 * cuando está todo aprobado: en ese caso el texto precargado queda tal cual,
 * afirmando que la documentación se verificó.
 */
export function bloqueVerificacionDocumental(items: ItemVerificacion[]): string {
  const problemas = items.filter((i) => i.estado !== 'APROBADA');
  if (problemas.length === 0) return '';

  const partes: string[] = [MARKER_VERIFICACION];

  // El encabezado nombra solo lo que efectivamente pasa, para no decirle al
  // Secretario que falta documentación cuando en realidad está toda cargada.
  const hay = (e: EstadoVerificacion) => problemas.some((p) => p.estado === e);
  const situaciones = [
    hay('RECHAZADA') ? 'documentación rechazada' : null,
    hay('SIN_PRESENTAR') ? 'documentación no presentada' : null,
    hay('SIN_VERIFICAR') ? 'documentación aún sin verificar' : null,
  ].filter(Boolean) as string[];
  const enumerado = situaciones.length === 1
    ? situaciones[0]
    : `${situaciones.slice(0, -1).join(', ')} y ${situaciones[situaciones.length - 1]}`;

  partes.push(
    'Se hace constar que **no toda la documentación se encuentra aprobada**: ' +
    `se registra ${enumerado}, según el siguiente detalle.`,
  );

  GRUPOS.forEach(({ estado, titulo }) => {
    const delGrupo = problemas.filter((p) => p.estado === estado);
    if (delGrupo.length === 0) return;
    partes.push(`*${titulo}:*`);
    partes.push(delGrupo
      .map((p) => `- ${p.label}${p.motivo ? ` — ${p.motivo}` : ''}`)
      .join('\n'));
  });

  return partes.join('\n\n');
}

/** Agrega (o quita, si no hay nada que informar) el bloque en el cuerpo dado. */
export function aplicarVerificacionDocumental(cuerpo: string, bloque: string): string {
  return aplicarBloqueGestionado(cuerpo, MARKER_VERIFICACION, bloque);
}
